import { default as Axios } from "axios";
import React, { useEffect } from "react";
import { ModalContext } from "../../Components/Modals";
import ModalLoginException, {
  ModalLoginExceptionProps,
} from "../../Components/Modals/ModalLoginException/modal-login-exception";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import AssetService from "./Asset/asset-api-service";
import FileService from "./File/file-api-service";
import IngestService from "./Ingest/ingest-api-service";
import JobBrokerService from "./JobBroker/job-broker-api-service";
import LogService from "./Log/log-api-service";
import MessengerService from "./Messenger/messenger-api-service";
import OrderService from "./Order/order-api-service";
import PlanningUserTaskService from "./PlanningUserTask/planning-user-task-api-service";
import SetupService from "./Setup/setup-api-service";
import SupportService from "./Support/support-api-service";
import ThumbnailService from "./Thumbnail/thumbnail-api-service";
import TitleService from "./Title/title-api-service";
import UserService from "./User/user-api-service";
import WorkflowService from "./Workflow/workflow-api-service";
import MediaService from "./Media/media-api-service";
import ShareService from "./Share/share-api-service";
import SearchService from "./Search/search-api-service";
import { CacheService } from "./LocalStorage/react-session-cache";
import SignalRNotifier from "./notifier-client";
import { PlatformAlertSeverity } from "./Types/platform-alert-severity";
import LegacyImporterService from "./LegacyImporter/legacy-importer-api-service";
import OktaService from "./Okta/okta-api-service";
import DciService from "./Dci/dci-api-service";
import DataService from "./Data/data-api-service";
import BillingService from "./Billing/billing-api-service";
import BasketService from "./Basket/basket-api-service";
import RightService from "./Right/right-api-service";
import UploadService from "./Upload/upload-api-service";
import HlsChunkerService from "./HlsChunker/hls-chunker-api-service";
import CockpitService from "./Cockpit/cockpit-api-service";
import AccessService from "./Access/access-api-service";

// API base urls
export const apiBaseUrls = {
  local: "http://localhost:",
  dev: "https://api-test.mediaspot.io/",
  prod: "https://api.mediaspot.io/",
};

export type EnvType = keyof typeof apiBaseUrls;
export const env: EnvType = process.env.REACT_APP_ENV as EnvType;

export type Hubs = "events" | "jobs" | "agents" | "activities" | "messenger" | "nle" | "calendar";

export const axiosForInterceptor = Axios.create({});

export const AxiosInterceptor = ({ children }: any) => {
  // Toaster
  const enqueueCustomSnackbar = useCustomSnackbar();
  // Modal
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const onClose: ModalLoginExceptionProps["onClose"] = (data) => {};

  const logged = cacheService.IsLoggedIn();

  // Alerts
  useEffect(() => {
    if (!logged) return;

    userService.GetActiveAlertsList().then(function (response) {
      if (response.success && response.alerts) {
        let color: any = "error";

        response.alerts.map((alert) => {
          if (alert.message) {
            switch (alert.severity) {
              case PlatformAlertSeverity.Alert:
                color = "warning";
                break;
              case PlatformAlertSeverity.Emmergency:
                color = "error";
                break;
              case PlatformAlertSeverity.Info:
                color = "primary";
                break;
            }
            enqueueCustomSnackbar(alert.subject || "", color, true, null, alert.message);
          }
        });
      }
    });
  }, [logged]);

  useEffect(() => {
    const resInterceptor = (data: any) => {
      return data;
    };

    const errorInterceptor = (error: any) => {
      // TODO : manage this kind of errors, maybe try to type error instead of :any
      if (error.code && error.code === "ERR_CANCELED") return Promise.reject(error);

      const regex = /\w+?-service/;
      let service = error.config.url.match(regex, "$&");
      let message = error.message + ` at ${service[0]}`;
      console.log(`Interceptor Error, message = [${error.message}]`);

      if (error.response?.data?.message !== undefined && error.response.data.message !== null) {
        console.log(`Interceptor Error, message = [${error.response.data.message}]`);
        message = error.response.data.message;
      }

      if (error.response?.data?.title) {
        message += ` : ${error.response.data.title}`;
      }

      // Temp code due to waiting merge of PR
      if (service[0] !== "access-service" && service[0] !== "asset-service" && env !== "prod") {
        if (error?.response?.status !== 500) enqueueCustomSnackbar(message, "error");
        else if (openModal) {
          const modal: ModalLoginExceptionProps = {
            onClose: onClose,
            title: error.message + ` at ${service[0]}`,
            message: error.response?.data?.message,
            advice: "If problem persists, please contact support. Thanks.",
          };

          openModal<ModalLoginExceptionProps>(ModalLoginException, modal);
        }
      }

      return Promise.reject(error);
    };
    const interceptor = axiosForInterceptor.interceptors.response.use(resInterceptor, errorInterceptor);
    return () => axiosForInterceptor.interceptors.response.eject(interceptor);
  }, [enqueueCustomSnackbar, openModal]);

  return children;
};

// Services
export const accessService = new AccessService(env);
export const assetService = new AssetService(env);
export const basketService = new BasketService(env);
export const billingService = new BillingService(env);
export const cacheService = new CacheService();
export const cockpitService = new CockpitService(env);
export const dataService = new DataService(env);
export const dciService = new DciService(env);
export const fileService = new FileService(env);
export const hlsChunkerService = new HlsChunkerService(env);
export const ingestService = new IngestService(env);
export const jobBrokerService = new JobBrokerService(env);
export const legacyImporterService = new LegacyImporterService(env);
export const logService = new LogService(env);
export const mediaService = new MediaService(env);
export const messengerService = new MessengerService(env);
export const oktaService = new OktaService(env);
export const orderService = new OrderService(env);
export const planningUserTaskService = new PlanningUserTaskService(env);
export const rightService = new RightService(env);
export const searchService = new SearchService(env);
export const setupService = new SetupService(env);
export const shareService = new ShareService(env);
export const supportService = new SupportService(env);
export const thumbnailService = new ThumbnailService(env);
export const titleService = new TitleService(env);
export const uploadService = new UploadService(env);
export const userService = new UserService(env);
export const workflowService = new WorkflowService(env);

// Events
export const activitiesNotifier = new SignalRNotifier(env, "activities");
export const agentsNotifier = new SignalRNotifier(env, "agents");
export const calendarNotifier = new SignalRNotifier(env, "calendar");
export const eventsNotifier = new SignalRNotifier(env, "events");
export const jobsNotifier = new SignalRNotifier(env, "jobs");
export const messengerNotifier = new SignalRNotifier(env, "messenger");
export const nleNotifier = new SignalRNotifier(env, "nle");
